import { LitElement, html } from 'lit';
import logoUrl from '../assets/logo.png';
import logoLightUrl from '../assets/logo-light.png';
import { api } from '../api.js';
import { passwordMeetsRules, passwordChecklist } from '../utils/password-policy.js';

const roleLabel = (r) =>
  r === 'owner' ? 'Eigenaar' : r === 'manager' ? 'Beheerder' : r === 'member' ? 'Lid' : (r || '');

/**
 * Public invitation accept page, reached at /accept-invite?token=...
 * Shows what the invite is for, then either accepts directly (existing account)
 * or creates a new account with a password. No auth required — the token is the
 * credential (GET/POST /api/invitations/*).
 */
export class AcceptInvite extends LitElement {
  createRenderRoot() { return this; }

  static properties = {
    token: { type: String },
    loading: { type: Boolean },
    info: { type: Object },
    error: { type: String },
    password: { type: String },
    password2: { type: String },
    formError: { type: String },
    busy: { type: Boolean },
    done: { type: Boolean },
  };

  constructor() {
    super();
    this.token = new URLSearchParams(window.location.search).get('token') || '';
    this.loading = true;
    this.info = null;
    this.error = null;
    this.password = '';
    this.password2 = '';
    this.formError = null;
    this.busy = false;
    this.done = false;
    this.isDarkMode = localStorage.getItem('mv_theme') === 'dark';
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadInfo();
  }

  async loadInfo() {
    if (!this.token) { this.error = 'Geen uitnodigingstoken gevonden in de link.'; this.loading = false; return; }
    try {
      this.info = await api.getInvitation(this.token);
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
    }
  }

  async accept(e) {
    e?.preventDefault?.();
    this.formError = null;
    if (!this.info.user_exists) {
      if (!passwordMeetsRules(this.password)) { this.formError = 'Je wachtwoord voldoet nog niet aan alle eisen.'; return; }
      if (this.password !== this.password2) { this.formError = 'De wachtwoorden komen niet overeen.'; return; }
    }
    this.busy = true;
    try {
      await api.acceptInvitation(this.token, this.info.user_exists ? undefined : this.password);
      this.done = true;
    } catch (err) {
      this.formError = err.message;
    } finally {
      this.busy = false;
    }
  }

  goToLogin() { window.location.href = '/'; }

  card(inner) {
    return html`
      <div style="min-height:100vh; display:flex; align-items:center; justify-content:center; padding:20px; background:var(--mv-bg,#f8f9fa);">
        <div style="width:100%; max-width:440px; background:var(--mv-surface,#fff); border:1px solid var(--mv-border); border-radius:16px; padding:32px; box-shadow:0 12px 40px rgba(0,0,0,0.08);">
          <img src="${this.isDarkMode ? logoLightUrl : logoUrl}" alt="GeoVoice" style="height:64px; width:auto; display:block; margin:0 auto 20px;">
          ${inner}
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return this.card(html`<div style="text-align:center; color:var(--mv-text-sub,#6c757d);">Uitnodiging laden…</div>`);
    }
    if (this.error || !this.info) {
      return this.card(html`
        <h2 style="margin:0 0 8px; text-align:center; color:var(--mv-text);">Uitnodiging niet beschikbaar</h2>
        <p style="text-align:center; color:var(--mv-text-sub,#6c757d);">${this.error || 'Onbekende fout.'}</p>
        <button @click=${this.goToLogin} style="width:100%; margin-top:12px; padding:11px; border-radius:8px; border:1px solid var(--mv-border); background:none; color:var(--mv-text); font-weight:600; cursor:pointer;">Naar inloggen</button>
      `);
    }
    if (!this.info.valid) {
      const msg = this.info.status === 'accepted' ? 'Deze uitnodiging is al geaccepteerd.'
        : this.info.status === 'revoked' ? 'Deze uitnodiging is ingetrokken.'
        : 'Deze uitnodiging is verlopen.';
      return this.card(html`
        <h2 style="margin:0 0 8px; text-align:center; color:var(--mv-text);">Uitnodiging niet (meer) geldig</h2>
        <p style="text-align:center; color:var(--mv-text-sub,#6c757d);">${msg}</p>
        <button @click=${this.goToLogin} style="width:100%; margin-top:12px; padding:11px; border-radius:8px; border:1px solid var(--mv-border); background:none; color:var(--mv-text); font-weight:600; cursor:pointer;">Naar inloggen</button>
      `);
    }
    if (this.done) {
      return this.card(html`
        <h2 style="margin:0 0 8px; text-align:center; color:var(--mv-text);">Welkom bij ${this.info.company_name}!</h2>
        <p style="text-align:center; color:var(--mv-text-sub,#6c757d);">Je bent toegevoegd als ${roleLabel(this.info.role)}. Log in met ${this.info.email} om verder te gaan.</p>
        <button @click=${this.goToLogin} style="width:100%; margin-top:12px; padding:11px; border-radius:8px; border:none; background:var(--mv-primary); color:#fff; font-weight:700; cursor:pointer;">Naar inloggen</button>
      `);
    }

    const newUser = !this.info.user_exists;
    return this.card(html`
      <h2 style="margin:0 0 4px; text-align:center; color:var(--mv-text);">Uitnodiging voor ${this.info.company_name}</h2>
      <p style="text-align:center; color:var(--mv-text-sub,#6c757d); margin:0 0 20px;">
        ${this.info.email} • rol: <strong style="color:var(--mv-text);">${roleLabel(this.info.role)}</strong>
      </p>

      <form @submit=${this.accept}>
        ${newUser ? html`
          <p style="font-size:0.88rem; color:var(--mv-text-sub,#6c757d); margin:0 0 12px;">Maak een wachtwoord aan om je account te activeren.</p>
          <label style="display:block; font-size:0.82rem; font-weight:700; color:var(--mv-text-sub,#6c757d); margin-bottom:4px;">Wachtwoord</label>
          <input type="password" required .value=${this.password} @input=${(e) => { this.password = e.target.value; }}
            style="width:100%; padding:10px 12px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-bg,#fff); color:var(--mv-text); margin-bottom:6px;" />
          ${passwordChecklist(this.password)}
          <label style="display:block; font-size:0.82rem; font-weight:700; color:var(--mv-text-sub,#6c757d); margin:14px 0 4px;">Herhaal wachtwoord</label>
          <input type="password" required .value=${this.password2} @input=${(e) => { this.password2 = e.target.value; }}
            style="width:100%; padding:10px 12px; border-radius:8px; border:1px solid var(--mv-border); background:var(--mv-bg,#fff); color:var(--mv-text); margin-bottom:16px;" />
        ` : html`
          <p style="font-size:0.88rem; color:var(--mv-text-sub,#6c757d); margin:0 0 16px;">Je hebt al een account. Accepteer de uitnodiging om toegang te krijgen tot dit bedrijf.</p>
        `}

        ${this.formError ? html`<div style="padding:10px 12px; border-radius:8px; background:rgba(220,53,69,0.1); color:var(--mv-danger,#dc3545); margin-bottom:14px; font-size:0.88rem;">${this.formError}</div>` : ''}

        <button type="submit" ?disabled=${this.busy}
          style="width:100%; padding:12px; border-radius:8px; border:none; background:var(--mv-primary); color:#fff; font-weight:700; cursor:pointer;">
          ${this.busy ? 'Bezig…' : (newUser ? 'Account aanmaken & accepteren' : 'Uitnodiging accepteren')}
        </button>
      </form>
    `);
  }
}

customElements.define('accept-invite', AcceptInvite);
